import axios from 'axios';

/**
 * Auth-specific axios instance.
 * Token is passed explicitly per call — NOT stored in interceptors.
 * This is used for login, refresh, logout, and /me — before the main
 * api.js instance has a token to inject.
 */
const authApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 20000,
  headers: { 'Content-Type': 'application/json' },
  // Architecture Rule 73 — the Authorized-Device gate sets a `device_id`
  // cookie on the 403 login response; the browser only stores/sends it on
  // credentialed requests. Backend CORS must have supports_credentials=true.
  withCredentials: true,
});

authApi.interceptors.request.use((config) => {
  const requestId = crypto.randomUUID();
  config.headers['X-Client-Request-Id'] = requestId;
  config._meta = { requestId, start: Date.now() };
  return config;
});

authApi.interceptors.response.use(
  (res) => {
    const ms = Date.now() - (res.config._meta?.start ?? 0);
    console.info(`[authApi] ${res.status} ${res.config.url} (${ms}ms)`);
    return res;
  },
  (err) => {
    const ms = Date.now() - (err.config?._meta?.start ?? 0);
    console.error(`[authApi] ${err.response?.status ?? 'ERR'} ${err.config?.url} (${ms}ms)`);
    err.friendlyMessage = err.response?.data?.message ?? 'Request failed.';
    return Promise.reject(err);
  },
);

const bearer = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

const OAUTH_CLIENT_ID     = process.env.REACT_APP_OAUTH_CLIENT_ID     ?? 'web';
const OAUTH_CLIENT_SECRET = process.env.REACT_APP_OAUTH_CLIENT_SECRET ?? null;

const clientCreds = () => {
  const body = { client_id: OAUTH_CLIENT_ID };
  if (OAUTH_CLIENT_SECRET) body.client_secret = OAUTH_CLIENT_SECRET;
  return body;
};

export const loginRequest         = (email, password, rememberMe = false) =>
  authApi.post('/auth/login', { ...clientCreds(), email, password, remember_me: Boolean(rememberMe) });
export const refreshRequest       = (refreshToken)          => authApi.post('/auth/refresh',          { ...clientCreds(), refresh_token: refreshToken });
export const meRequest            = (token)                 => authApi.get('/auth/me',                bearer(token));
export const logoutRequest        = (token)                 => authApi.post('/auth/logout',           {}, bearer(token));
export const logoutAllRequest     = (token)                 => authApi.post('/auth/logout-all',       {}, bearer(token));
export const changePasswordRequest= (token, data)           => authApi.post('/auth/change-password',  data, bearer(token));
export const uploadProfilePhotoRequest = (token, file) => {
  const fd = new FormData();
  fd.append('profile_photo', file);
  // authApi defaults to Content-Type: application/json — the explicit
  // `undefined` here forces axios to STRIP that default so it can set
  // `multipart/form-data; boundary=...` itself from the FormData body.
  // Without this, Laravel never parses the upload and the request fails
  // validation with "The profile photo field is required."
  return authApi.post('/auth/profile/photo', fd, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': undefined,
    },
  });
};
export const listSessionsRequest  = (token, params = {})    => authApi.get('/auth/sessions',          { ...bearer(token), params });
export const revokeSessionRequest = (token, sessionId)      => authApi.delete(`/auth/sessions/${sessionId}`, bearer(token));
export const forgotPasswordRequest= (email)                 => authApi.post('/auth/password/forgot',   { email });
export const resetPasswordRequest = (data)                  => authApi.post('/auth/password/reset',    data);
export const verifyEmailRequest   = (token)                 => authApi.get(`/auth/email/verify/${encodeURIComponent(token)}`);
export const resendVerificationRequest = (token)            => authApi.post('/auth/email/verify-resend', {}, bearer(token));
export const mfaEnrollRequest     = (token)                 => authApi.post('/auth/mfa/enroll',        {}, bearer(token));
export const mfaVerifySetupRequest= (token, code)           => authApi.post('/auth/mfa/verify-setup',  { code }, bearer(token));
export const mfaDisableRequest    = (token, currentPassword)=> authApi.post('/auth/mfa/disable',       { current_password: currentPassword }, bearer(token));
export const mfaRegenerateBackupCodesRequest = (token)      => authApi.post('/auth/mfa/backup-codes/regenerate', {}, bearer(token));

/**
 * Complete an MFA-gated login (Architecture Rule 43).
 *
 * - challengeToken: the `mfa_challenge_token` returned by /auth/login when the
 *   user has MFA enabled. Single-use, 5-minute TTL.
 * - code:           the user's 6-digit TOTP code OR a 10-char backup code.
 * - codeType:       'totp' | 'backup_code'.
 *
 * On success returns the standard token envelope (same shape as /auth/login
 * for non-MFA users), and the SPA resumes the normal session flow.
 */
export const mfaChallengeRequest = (challengeToken, code, codeType = 'totp', rememberMe = false) =>
  authApi.post('/auth/mfa/challenge', {
    mfa_challenge_token: challengeToken,
    code,
    code_type: codeType,
    remember_me: Boolean(rememberMe),
  });
